import { css } from "@emotion/react";

/**
 * Compact mode for the Bingo card grid.
 *
 * A fan can raise the device or browser text size. That changes the browser
 * default font size. Every `rem` in the app follows it. The card grid cannot
 * grow with the text, because five tiles must fit across the viewport. So the
 * grid needs a second layout for the case "the viewport is short of room".
 *
 * Inside a media query, `rem` resolves against the browser default font size.
 * It does not use the `html` declaration. So `min-width: 20rem` is true on a
 * 390px phone at the default text size (20rem = 320px) and false at 1.5x
 * (20rem = 480px). The query below is therefore true when either the screen
 * is small or the text is large. That is exactly the case the grid needs.
 *
 * The query is written as the complement of the existing `min-width: 20rem`
 * tier. A 320px phone at the default text size matches `min-width: 20rem`, so
 * it keeps the layout it has today.
 *
 * See docs/adr/0015-compact-card-grid-for-large-text.md.
 */
export const COMPACT_MEDIA_QUERY_LIST = "not all and (min-width: 20rem)";

/**
 * Compact styles select on `html[data-compact]`, not on the media query.
 * compact-mode.ts sets the attribute from the media query and from the
 * measured text zoom, so a host that enlarges text without a change to the
 * default font size gets the same layout. See ADR-015, amendment 1.
 */
export const COMPACT_SELECTOR = "html[data-compact] &";

/**
 * Divide a compact px size by the measured text zoom. The host multiplies
 * rendered text by that zoom after CSS resolves, so the quotient renders at
 * the compact value. The variable is absent at the default text size.
 */
export const perTextZoom = (px: number) =>
  `calc(${px}px / var(--text-zoom, 1))`;

/**
 * Tile values in compact mode. All values are in px on purpose. A `rem`
 * value would grow with the text size and remove the room it must provide.
 * The tile cannot grow with the text, so the text stops here.
 */
export const COMPACT_TILE = {
  /** Gap between tiles. The page background shows through it as the line. */
  gapPx: 2,
  /**
   * Tile height as a multiple of tile width. This is the ratio the grid
   * used below the 18rem tier before compact mode existed. It gives one more
   * line of text than a square tile.
   */
  aspectRatio: 1.3,
  /**
   * The largest size at which five lines fit a tile on a 390px viewport. The
   * tile is about 99px tall there. Five lines of 17px plus 6px of padding
   * take 91px. See compact.test.ts.
   */
  fontSizePx: 14,
  /** About 1.2 times the font size, rounded to a whole px. */
  lineHeightPx: 17,
  /** Room between the text and the tile edge. */
  paddingPx: 3,
  /** The word FREE on a free square with no image. The 1x size. */
  freeSpaceFontSizePx: 16,
} as const;

/**
 * The text metrics of a tile in compact mode. Every tile surface and every
 * `TileProbe` includes this, so the measured line count matches the tile.
 */
export const compactTileText = css`
  ${COMPACT_SELECTOR} {
    font-size: ${perTextZoom(COMPACT_TILE.fontSizePx)};
    /* Unitless, so the line follows the rendered font size under a zoom. */
    line-height: ${COMPACT_TILE.lineHeightPx / COMPACT_TILE.fontSizePx};
    padding: ${COMPACT_TILE.paddingPx}px;
  }
`;

/**
 * The text scale at which compact mode starts on a 390px phone. 20rem is
 * 480px at 1.5x, which is wider than the phone. So the header type stops at
 * its 1.5x size. The cap changes nothing at 1.5x and recovers room above it.
 */
const COMPACT_TEXT_SCALE_CAP = 1.5;

/**
 * A text length that follows the text size up to `COMPACT_TEXT_SCALE_CAP`.
 * `rem` is a preset value such as `HEADING_H3.fontSize`, so the cap has one
 * source of truth. The `rem` term keeps the 1x value on a phone narrower
 * than 320px, where compact mode fires at the default text size. A plain px
 * value would enlarge the text there.
 */
export const compactTextCap = (rem: `${number}rem`) =>
  `min(${rem}, ${perTextZoom(Number.parseFloat(rem) * 16 * COMPACT_TEXT_SCALE_CAP)})`;

/**
 * The gap between the card header rows, and between the header, the grid
 * and the pattern buttons, in compact mode. The play screen has a fixed
 * budget: a 390x844 viewport must show all three. At 2x the header stacks to
 * three rows and a rem gap doubles. So the gap stops at its 1x size, 1rem.
 */
export const COMPACT_GAP_PX = 16;
